import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingCart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMerchCart } from "@/contexts/MerchCartContext";
import { displayPrice } from "@/config/merchProducts";
import { detectUserCurrency } from "@/lib/currencyDetection";
import { MerchCartCheckoutDialog } from "./MerchCartCheckoutDialog";

export function MerchCartDrawer() {
  const { t } = useTranslation();
  const { items, isOpen, setOpen, setQuantity, removeItem, subtotalDKK } = useMerchCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [currency] = useState(() => detectUserCurrency());

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-primary" />
              {t("merch.cart.title")}
            </SheetTitle>
          </SheetHeader>

          {items.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
              <ShoppingCart className="h-10 w-10 text-muted-foreground/50" />
              <p className="font-medium text-foreground">{t("merch.cart.empty")}</p>
              <p className="text-sm text-muted-foreground">{t("merch.cart.emptyHint")}</p>
            </div>
          ) : (
            <>
              <div className="flex-1 space-y-3 overflow-y-auto py-4">
                {items.map((item) => {
                  const variant = [item.size, item.color, item.gender === "women" ? t("merch.sampleOrder.women") : null]
                    .filter(Boolean)
                    .join(" / ");
                  return (
                    <div key={item.key} className="flex gap-3 rounded-lg border border-border/50 bg-card/50 p-3">
                      <img src={item.image} alt={item.name} className="h-16 w-16 rounded-md object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{item.shortName}</p>
                        {variant && <p className="text-xs text-muted-foreground">{variant}</p>}
                        <p className="mt-0.5 text-sm font-bold text-primary">
                          {displayPrice(item.priceDKK, currency)}
                        </p>
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="flex items-center rounded-md border border-border">
                            <button
                              className="px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                              onClick={() => setQuantity(item.key, item.quantity - 1)}
                              aria-label={t("merch.cart.decrease")}
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="min-w-6 text-center text-xs font-medium">{item.quantity}</span>
                            <button
                              className="px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
                              onClick={() => setQuantity(item.key, item.quantity + 1)}
                              disabled={item.quantity >= 5}
                              aria-label={t("merch.cart.increase")}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <button
                            className="ml-auto text-muted-foreground hover:text-destructive"
                            onClick={() => removeItem(item.key)}
                            aria-label={t("merch.cart.remove")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="space-y-3 border-t border-border pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("merch.cart.subtotal")}</span>
                  <span className="font-bold text-foreground">{displayPrice(subtotalDKK, currency)}</span>
                </div>
                <div className="flex justify-between gap-4 text-sm text-muted-foreground">
                  <span>{t("merch.checkoutDialog.processingFee")}</span>
                  <span className="shrink-0">{t("merch.cart.calculatedAtCheckout")}</span>
                </div>
                <p className="text-xs text-muted-foreground">{t("merch.cart.shippingAtCheckout")}</p>
                <Button className="w-full gap-2" onClick={() => setCheckoutOpen(true)}>
                  <ShoppingCart className="h-4 w-4" />
                  {t("merch.cart.checkout")}
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <MerchCartCheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} currency={currency} />
    </>
  );
}
